import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { JPOnboardingSteps } from './use-job-positions-onboarding';
import tw, { styled } from 'twin.macro';
import {
  advertisedLocationField,
  domainInterestField,
  positiveSalaryField,
  restaurantIdField,
  staffRequestedField,
  workerMaxDistanceField,
} from './validation-fields';
import { isItalianPayTransparencyFlow } from '../../libs/salary/is-italian-venue';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ValidationSchema = Yup.ObjectSchema<any>;

/**
 * Step-specific validation schemas
 * Each step validates only the fields that are shown/required in that step
 *
 * @param t - Translation function from useTranslation hook
 * @returns Record of validation schemas for each step
 */
export const getStepValidationSchemas = (t: TFunction): Record<JPOnboardingSteps, ValidationSchema> => ({
  venue_selection: Yup.object().shape({
    restaurant_id: restaurantIdField(t),
  }) as ValidationSchema,

  venue_onboarding: Yup.object().shape({
    // No validation needed - venue onboarding has its own internal validation
  }) as ValidationSchema,

  role: Yup.object().shape({
    domain_interest: domainInterestField(t),
    occupation_id: Yup.string().required(t('steps.role.occupation_required')),
    experience: Yup.string().required(t('steps.role.experience_required')),
    experience_var: Yup.mixed().nullable(),
    experience_specifics: Yup.string()
      .nullable()
      .when('experience_var', {
        is: (val: { key?: string } | null) => val?.key && ['zero', 'experience_jp_zero'].includes(val.key),
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.required(t('steps.role.experience_specifics_required')),
      }),
    kitchen_station: Yup.string()
      .nullable()
      .max(200, t('errors.max_length', { count: 200 })),
  }) as ValidationSchema,

  skills: Yup.object().shape({
    languages: Yup.array()
      .of(
        Yup.object().shape({
          language: Yup.string().required(t('errors.required_field')),
          level: Yup.string().required(t('errors.required_field')),
        })
      )
      .nullable(),
    // Add validation for skills fields
    // required_skills: Yup.array().min(1, 'Please select at least one skill'),
  }) as ValidationSchema,

  other_requirements: Yup.object().shape(
    {
      staff_requested: staffRequestedField(t),
      advertised_location: advertisedLocationField(t),
      worker_max_distance: workerMaxDistanceField(t).when('advertised_location', {
        is: 'local',
        then: (schema) => schema.required(t('errors.required_field')),
        otherwise: (schema) => schema.nullable(),
      }),
      ['seasonal?']: Yup.boolean().required(t('errors.required_field')),
      desired_start_type: Yup.string()
        .nullable()
        .when('seasonal?', {
          is: false,
          then: (schema) =>
            schema
              .required(t('errors.required_field'))
              .oneOf(['asap', 'exact_date', 'indicative_date'], t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      desired_start_date: Yup.string()
        .nullable()
        .when('desired_start_type', {
          is: (val: string) => val === 'exact_date' || val === 'indicative_date',
          then: (schema) => schema.required(t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      season_starts_at: Yup.string()
        .nullable()
        .when('seasonal?', {
          is: true,
          then: (schema) => schema.required(t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      season_ends_at: Yup.string()
        .nullable()
        .when('seasonal?', {
          is: true,
          then: (schema) => schema.required(t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      accommodation_choice: Yup.string()
        .nullable()
        .oneOf(['single', 'shared', 'none', null], t('errors.required_field'))
        .when(['seasonal?', 'season_starts_at', 'season_ends_at', 'desired_start_type', 'desired_start_date'], {
          is: (
            seasonal: boolean | null | undefined,
            seasonStartsAt: string | null | undefined,
            seasonEndsAt: string | null | undefined,
            desiredStartType: string | null | undefined,
            desiredStartDate: string | null | undefined
          ) => {
            const isChooseDate = desiredStartType === 'exact_date' || desiredStartType === 'indicative_date';
            return (
              (seasonal === true && !!seasonStartsAt && !!seasonEndsAt) ||
              (seasonal === false && (desiredStartType === 'asap' || (isChooseDate && !!desiredStartDate)))
            );
          },
          then: (schema) => schema.required(t('errors.required_field')),
          otherwise: (schema) => schema.nullable(),
        }),
      accommodation_details: Yup.string()
        .nullable()
        .max(500, t('errors.max_length', { count: 500 })),
    },
    [['desired_start_type', 'desired_start_date']]
  ) as ValidationSchema,

  contract_and_shifts: Yup.object().shape({
    contract_type: Yup.string().required(t('errors.required_field')),
    contract_type_var: Yup.mixed().nullable(),
    job_type: Yup.string().required(t('errors.required_field')),
    job_type_var: Yup.mixed().nullable(),
    days_required_per_week_number: Yup.number().required(t('errors.required_field')),
    shift_category: Yup.string().required(t('errors.required_field')),
    // Required only when job_type is part_time AND contract_type is not on_call.
    // Full-time implies 40h. On-call employers can't commit to a weekly figure.
    // Safe fallback: if either _var key is missing (e.g. edit mode loading a JP
    // whose backend payload didn't hydrate the variable objects, or a step
    // visited before contract_type/job_type are picked), treat the field as
    // optional rather than risk a false-positive required error.
    part_time_weekly_hours: Yup.string()
      .nullable()
      .max(200, t('errors.max_length', { count: 200 }))
      .when(['job_type_var', 'contract_type_var'], {
        is: (jobTypeVar: { key?: string } | null, contractTypeVar: { key?: string } | null) =>
          !!jobTypeVar?.key &&
          !!contractTypeVar?.key &&
          jobTypeVar.key === 'part_time' &&
          contractTypeVar.key !== 'on_call',
        then: (schema) => schema.required(t('errors.required_field')),
        otherwise: (schema) => schema.nullable(),
      }),
  }) as ValidationSchema,

  salary: Yup.object()
    .shape({
      payment_type: Yup.string().required(t('errors.required_field')),
      payment_type_var: Yup.mixed().nullable(),
      min_salary: positiveSalaryField(t),
      max_salary: positiveSalaryField(t),
      salary: positiveSalaryField(t),
      // salary_type: Yup.string().required(t('errors.required_field')),
      // salary_month_number: Yup.number().required(t('errors.required_field')),
    })
    .test('salary-validation', '', function (value) {
      const { salary, min_salary, max_salary } = value || {};

      // If exact salary is provided, min and max are not required
      if (salary !== null && salary !== undefined) {
        return true;
      }

      const hasMin = min_salary !== null && min_salary !== undefined;
      const hasMax = max_salary !== null && max_salary !== undefined;

      // Min only or both min+max - OK
      if (hasMin) {
        return true;
      }

      // Only max without min - blocked ("fino a..." is not allowed)
      if (hasMax && !hasMin) {
        return this.createError({
          path: 'min_salary',
          message: t('steps.salary.salary_min_required'),
        });
      }

      // Neither salary nor min/max are provided - validation fails
      const error = this.createError({
        message: t('errors.required_field'),
      });
      error.inner = [
        this.createError({
          path: 'salary',
          message: t('errors.required_field'),
        }),
        this.createError({
          path: 'min_salary',
          message: t('errors.required_field'),
        }),
        this.createError({
          path: 'max_salary',
          message: t('errors.required_field'),
        }),
      ];
      return error;
    })
    .test('salary-sanity-check', '', function (value) {
      const { salary, min_salary, max_salary, payment_type_var } = value || {};
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const paymentTypeLabel = (payment_type_var as any)?.label?.toLowerCase() || '';
      const isHourly = paymentTypeLabel.includes('ore') || paymentTypeLabel.includes('orari');
      const isAnnual = paymentTypeLabel.includes('annual') || paymentTypeLabel.includes('ral');
      const threshold = isHourly ? 4 : isAnnual ? 5000 : 100;
      const errorMsg = isHourly
        ? t('steps.salary.salary_too_low_hourly')
        : isAnnual
        ? t('steps.salary.salary_too_low_annual')
        : t('steps.salary.salary_too_low_monthly');

      const valuesToCheck = [salary, min_salary, max_salary].filter(
        (v): v is number => v !== null && v !== undefined && v > 0
      );

      if (valuesToCheck.length === 0) return true;

      const errors: Yup.ValidationError[] = [];
      if (salary !== null && salary !== undefined && salary > 0 && salary < threshold) {
        errors.push(this.createError({ path: 'salary', message: errorMsg }));
      }
      if (min_salary !== null && min_salary !== undefined && min_salary > 0 && min_salary < threshold) {
        errors.push(this.createError({ path: 'min_salary', message: errorMsg }));
      }
      if (max_salary !== null && max_salary !== undefined && max_salary > 0 && max_salary < threshold) {
        errors.push(this.createError({ path: 'max_salary', message: errorMsg }));
      }

      if (errors.length > 0) {
        const error = this.createError({ message: errorMsg });
        error.inner = errors;
        return error;
      }

      return true;
    })
    .test('range-order', '', function (value) {
      // A salary range must be ordered: the maximum can't be lower than the minimum.
      const { salary, min_salary, max_salary } = value || {};
      if (salary !== null && salary !== undefined) return true; // exact mode, no range
      const hasMin = min_salary !== null && min_salary !== undefined;
      const hasMax = max_salary !== null && max_salary !== undefined;
      if (hasMin && hasMax && (max_salary as number) < (min_salary as number)) {
        return this.createError({
          path: 'max_salary',
          message: t('steps.salary.salary_max_below_min'),
        });
      }
      return true;
    })
    .test('italian-range-requires-both-bounds', '', function (value) {
      // Italian pay-transparency flow only. Art. 5 D.Lgs. 96/2026 asks for the initial pay or
      // its "fascia" (a bounded band), so a range must carry BOTH ends; an open-ended "da X"
      // (min only) isn't a fascia. Requiring both also avoids the estimate/confirmation deadlock
      // (estimate -> card -> confirm). The legacy/foreign flow still allows min-only "da X".
      const data = value as {
        salary?: number | null;
        min_salary?: number | null;
        max_salary?: number | null;
        restaurant?: { country?: string };
      };
      if (!isItalianPayTransparencyFlow(data?.restaurant?.country)) return true;
      const { salary, min_salary, max_salary } = data || {};
      if (salary !== null && salary !== undefined) return true; // exact figure, no range
      const hasMin = min_salary !== null && min_salary !== undefined;
      const hasMax = max_salary !== null && max_salary !== undefined;
      if (hasMin && !hasMax) {
        return this.createError({
          path: 'max_salary',
          message: t('steps.salary.salary_max_required'),
        });
      }
      return true;
    })
    .test('estimates-confirmed', '', function (value) {
      // Italian pay-transparency flow only: the employer must confirm the estimated
      // gross/net values before leaving the step. Foreign/legacy flows are unaffected.
      const data = value as { restaurant?: { country?: string }; salary_estimates_confirmed?: boolean } | undefined;
      if (!isItalianPayTransparencyFlow(data?.restaurant?.country)) return true;
      if (data?.salary_estimates_confirmed === true) return true;
      return this.createError({
        path: 'salary_estimates_confirmed',
        message: t('steps.salary.estimates_confirm_required'),
      });
    }) as ValidationSchema,

  benefits: Yup.object().shape({
    // Add validation for benefits fields (usually optional)
    // Example:
    // benefits: Yup.array(),
  }) as ValidationSchema,

  additional_notes: Yup.object().shape({
    // No validation needed - notes are optional
  }) as ValidationSchema,

  ai_autogeneration: Yup.object().shape({
    job_title: Yup.string().required(t('errors.required_field')),
    job_description: Yup.string().required(t('errors.required_field')),
    job_contract_description: Yup.string().required(t('errors.required_field')),
  }) as ValidationSchema,

  review: Yup.object().shape({
    // Final review step - validate all required fields are present
    // This ensures nothing was missed
    restaurant_id: restaurantIdField(t),
    domain_interest: domainInterestField(t),
    // Add other critical fields that must be present
  }) as ValidationSchema,

  company_and_payment: Yup.object().shape({
    // No validation needed - handled via sub-views within the step
  }) as ValidationSchema,

  success: Yup.object().shape({
    // No validation needed - success is a read-only confirmation screen
  }) as ValidationSchema,
});

/**
 * Helper function to get validation schema for a specific step
 * @param step - The current onboarding step
 * @param t - Translation function from useTranslation hook
 */
export const getValidationSchemaForStep = (step: JPOnboardingSteps, t: TFunction): ValidationSchema => {
  const schemas = getStepValidationSchemas(t);
  return schemas[step];
};

/**
 * Complete validation schema for the entire form
 * Used only when submitting the final form
 * @param t - Translation function from useTranslation hook
 */
export const getCompleteJobPositionSchema = (t: TFunction) =>
  Yup.object().shape({
    restaurant_id: restaurantIdField(t),
    domain_interest: domainInterestField(t),
    // Add all other required fields from all steps
    // occupation_id: Yup.string().required(t('errors.occupation_required')),
    // contract_type: Yup.string().required(t('errors.contract_type_required')),
    // salary_min: Yup.number().required(t('errors.salary_min_required')).positive(t('errors.salary_positive')),
    // ... etc
  });

export const ErrorMessage = styled.span`
  ${tw`mt-1 text-sm text-red-600 font-normal`}
`;
